import { FC } from "react";
import { IDashboardCard } from "../../interfaces/components/Dashboard.types";
import { AnalyticsModel } from "../../interfaces/endpoints/IAnalytics.types";
import { cx } from "../../utils/classnames";
import Animation from "../ui/Animation";
import Box from "../ui/Box";
import InfoTip from "../ui/InfoTip";

const DashboardCard: FC<IDashboardCard> = ({
  title,
  currentValue,
  ratePercentage,
  ratePercentageDesc,
  className,
  border,
  padding = "px-6 py-4",
  rounded,
  animation = true,
  loading,
  delay,
  duration = 400,
  type = "fade-in",
  currency = "€",
  breakdown,
  info
}) => {
  return (
    <Animation show={true} type={type} duration={duration} delay={delay}>
      <div className={cx(["w-full group", className])}>
        <Box
          className={cx([
            "w-full h-full",
            animation &&
            "transform-gpu transition-all duration-500 group-hover:shadow-xl"
          ])}
          border={border}
          rounded={rounded}
          shadow={false}
          padding={padding}
        >
          <div className="flex items-center gap-1 mb-1">
            <p
              className={cx([
                "flex text-neutral font-medium leading-[18px] text-xs",
                animation &&
                "transform-gpu transition-all duration-400 group-hover:text-primary"
              ])}
            >
              {title}
            </p>
            {info && <InfoTip text={info} className="w-3.5 h-3.5" />}
          </div>
          <div className="flex">
            <div className="text-black text-xl font-medium mb-1 leading-[30px] w-full empty:h-[30px]">
                {loading ? (
                <div className="w-1/3 h-[30px] bg-gray-300 animate-pulse rounded-sm" />
                ) : currentValue !== undefined ? (
                `${typeof currentValue === "number" ? (currentValue as number).toLocaleString() :
                  currentValue} ${currency ? currency : ""}`
                ) : null}
            </div>
          </div>
          <div className="leading-[18px] text-xs empty:h-[18px]">
            {loading ? (
              <div className="w-full h-[18px] bg-gray-300 animate-pulse rounded-sm" />
            ) : (
              <>
                {ratePercentage === null ? (
                  // The period before had nothing, so there is no change to state — say that rather
                  // than print a number or a sign next to a sentence about the period before.
                  <span className="font-medium text-neutral">
                    nuk ka të dhëna për krahasim
                  </span>
                ) : ratePercentage !== undefined ? (
                  <>
                    <span
                      className={cx([
                        "inline-flex mr-2 font-bold",
                        animation &&
                        "transform-gpu transition-all duration-500 scale-100 group-hover:scale-[1.20]",
                        ratePercentage.startsWith("-")
                          ? "text-red"
                          : "text-confirm"
                      ])}
                      style={{ willChange: "transform", backfaceVisibility: "hidden" }}
                    >
                      {ratePercentage}%
                    </span>
                    <span className="font-medium text-neutral">
                      {ratePercentageDesc}
                    </span>
                  </>
                ) : null}
              </>
            )}
          </div>
          {breakdown && !loading && breakdown.length > 0 ? (
            <dl
              className="mt-3 pt-2.5 border-t border-gray-200 grid grid-cols-2
               gap-x-4 gap-y-1 text-[11px] leading-4"
            >
              {breakdown.map((row) => (
                <div key={row.label} className="flex justify-between gap-2 min-w-0">
                  <dt className="text-neutral truncate" title={row.label}>
                    {row.label}
                  </dt>
                  <dd className="text-black font-medium whitespace-nowrap">
                    {`${row.value < 0 ? "−" : ""}${Math.abs(row.value).toLocaleString(undefined, {
                      maximumFractionDigits: 2
                    })} ${currency || ""}`}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
        </Box>
      </div>
    </Animation>
  );
};

// The rate a card shows for a metric: the backend's value, which is null when the period before had
// nothing to compare with (the card then says there is no data to compare), or "0" while there is no
// response.
export const rateOf = (metric?: AnalyticsModel) =>
  metric ? metric.ratePercentage : "0";

export default DashboardCard;
